import React, { useEffect, useState } from "react";
import Button from "../../components/Button/Button";
import ButtonEdit from "../../components/Button/ButtonEdit";
import ButtonDelete from "../../components/Button/ButtonDelete";
import { FaPlus, FaTimes, FaUsers, FaSave } from "react-icons/fa";
import {
  addActeursToGroup,
  removeActeursFromGroup,
  editActeurGroup,
} from "../../services/acteursServices";
import { toast } from "react-toastify";

const ManageGroupModal = ({
  isOpen,
  onClose,
  acteurId,
  acteurNom,
  acteurPrenom,
  groups,
  acteurGroups,
  refreshData,
}) => {
  const [localActeurGroups, setLocalActeurGroups] = useState([]);
  const [selectedGroupToAdd, setSelectedGroupToAdd] = useState("");
  const [isResponsable, setIsResponsable] = useState(false);
  const [poste, setPoste] = useState("");
  const [editedGroupId, setEditedGroupId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setLocalActeurGroups(Array.isArray(acteurGroups) ? acteurGroups : []);
      setSelectedGroupToAdd("");
      setIsResponsable(false);
      setPoste("");
      setEditedGroupId(null);
    }
  }, [isOpen, acteurGroups]);

  if (!isOpen) return null;
  if (!acteurId) return null;

  const handleAddGroup = async () => {
    if (!selectedGroupToAdd) {
      toast.error("Sélectionnez d'abord un groupe.");
      return;
    }
    try {
      await addActeursToGroup(acteurId, selectedGroupToAdd, isResponsable, poste);
      toast.success("Acteur ajouté au groupe avec succès");
      await refreshData();
      onClose();
    } catch (error) {
      toast.error("Erreur lors de l'ajout de l'acteur au groupe");
      console.error("Erreur lors de l'ajout de l'acteur au groupe:", error);
    }
  };

  const handleRemoveGroup = async (groupId) => {
    try {
      await removeActeursFromGroup(acteurId, groupId);
      toast.success("Acteur supprimé du groupe avec succès");
      setLocalActeurGroups((prev) => prev.filter((g) => g.id !== groupId));
      await refreshData();
    } catch (error) {
      toast.error("Erreur lors de la suppression de l'acteur du groupe");
      console.error("Erreur lors de la suppression de l'acteur du groupe:", error);
    }
  };

  const handleEditGroup = (groupId) => {
    const group = localActeurGroups.find((g) => g.id === groupId);
    if (!group) return;
    setEditedGroupId(groupId);
    setIsResponsable(!!group.ActeurGroup?.is_responsable);
    setPoste(group.ActeurGroup?.poste || "");
  };

  const handleSaveEditGroup = async () => {
    if (!editedGroupId) return;
    try {
      await editActeurGroup(acteurId, editedGroupId, isResponsable, poste);
      toast.success("Acteur modifié dans le groupe avec succès");
      const updatedGroups = localActeurGroups.map((g) =>
        g.id === editedGroupId
          ? {
              ...g,
              ActeurGroup: {
                ...(g.ActeurGroup || {}),
                is_responsable: isResponsable,
                poste,
              },
            }
          : g
      );
      setLocalActeurGroups(updatedGroups);
      setEditedGroupId(null);
      setIsResponsable(false);
      setPoste("");
      await refreshData();
    } catch (error) {
      toast.error("Erreur lors de la modification de l'acteur dans le groupe");
      console.error(
        "Erreur lors de la modification de l'acteur dans le groupe:",
        error
      );
    }
  };

  const handleClose = () => {
    setEditedGroupId(null);
    setSelectedGroupToAdd("");
    setIsResponsable(false);
    setPoste("");
    onClose();
  };

  const availableGroups =
    Array.isArray(groups) && groups.length > 0
      ? groups.filter(
          (g) => !localActeurGroups.some((ag) => ag.id === g.id)
        )
      : [];

  const editedGroup = editedGroupId
    ? localActeurGroups.find((g) => g.id === editedGroupId)
    : null;

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 px-2">
      <div className="modal-box bg-white dark:bg-slate-900 p-0 rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden">
        {/* HEADER */}
        <div className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-500 px-5 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-base sm:text-lg font-bold uppercase tracking-[0.18em] text-white">
              Groupes de travail
            </h3>
            <p className="mt-1 text-xs text-white/85">
              {acteurNom?.toUpperCase()} {acteurPrenom}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/15 text-white">
              <FaUsers />
            </span>
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/20 text-white hover:bg-black/30"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* BODY */}
        <div className="px-5 py-4 sm:px-6 sm:py-5 space-y-4">
          {/* Groupes actuels */}
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 mb-2">
              Groupes actuels
            </h4>
            {localActeurGroups.length === 0 ? (
              <div className="text-xs text-slate-500 dark:text-slate-300 italic">
                Cet acteur n'est associé à aucun groupe.
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {localActeurGroups.map((group) => (
                  <div
                    key={group.id}
                    className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
                  >
                    <div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-50">
                        {group.nom}
                      </div>
                      <div className="text-[0.7rem] text-slate-500 dark:text-slate-300 mt-0.5">
                        {group.ActeurGroup?.is_responsable && (
                          <span className="inline-flex items-center rounded-full bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200 px-2 py-0.5 mr-2">
                            Responsable
                          </span>
                        )}
                        {group.ActeurGroup?.poste && (
                          <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 dark:bg-slate-900/40 dark:text-slate-200 px-2 py-0.5">
                            Poste : {group.ActeurGroup.poste}
                          </span>
                        )}
                        {!group.ActeurGroup?.is_responsable &&
                          !group.ActeurGroup?.poste && (
                            <span className="italic">Membre</span>
                          )}
                      </div>
                    </div>
                    <div className="flex gap-1 sm:justify-end">
                      <ButtonEdit
                        onClick={() => handleEditGroup(group.id)}
                        hoverLabel="Modifier rôle dans ce groupe"
                      />
                      <ButtonDelete
                        onClick={() => handleRemoveGroup(group.id)}
                        hoverLabel="Retirer du groupe"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* LIGNE DE SÉPARATION */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-3" />

          {/* Bloc édition OU ajout */}
          {editedGroupId && editedGroup ? (
            <>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 mb-2">
                Modifier le rôle dans le groupe
              </h4>
              <div className="rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 px-3 py-3 space-y-3">
                <div className="text-xs text-slate-600 dark:text-slate-300">
                  Groupe sélectionné :{" "}
                  <span className="font-semibold">
                    {editedGroup.nom}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit-isResponsable"
                    checked={isResponsable}
                    onChange={(e) => setIsResponsable(e.target.checked)}
                    className="checkbox checkbox-sm"
                  />
                  <label
                    htmlFor="edit-isResponsable"
                    className="text-xs text-slate-700 dark:text-slate-200"
                  >
                    Responsable du groupe
                  </label>
                </div>
                <div>
                  <label
                    htmlFor="edit-poste"
                    className="block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 mb-1"
                  >
                    Poste (optionnel)
                  </label>
                  <input
                    id="edit-poste"
                    type="text"
                    value={poste}
                    onChange={(e) => setPoste(e.target.value)}
                    className="input input-bordered w-full text-sm bg-white dark:bg-slate-900 dark:text-slate-50"
                    placeholder="Ex : Responsable arbitrage, membre commission..."
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    onClick={() => {
                      setEditedGroupId(null);
                      setIsResponsable(false);
                      setPoste("");
                    }}
                    className="btn-ghost gap-2 !px-3 !py-1.5"
                    text="Annuler"
                    icon={FaTimes}
                  />
                  <Button
                    onClick={handleSaveEditGroup}
                    className="btn-primary gap-2 !px-3 !py-1.5"
                    text="Enregistrer"
                    icon={FaSave}
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 mb-2">
                Ajouter à un nouveau groupe
              </h4>
              <div className="rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 px-3 py-3 space-y-3">
                <div>
                  <label
                    className="block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 mb-1"
                    htmlFor="group-select"
                  >
                    Groupe
                  </label>
                  <select
                    id="group-select"
                    value={selectedGroupToAdd}
                    onChange={(e) => setSelectedGroupToAdd(e.target.value)}
                    className="input input-bordered w-full text-sm bg-white dark:bg-slate-900 dark:text-slate-50"
                  >
                    <option value="">Sélectionner un groupe</option>
                    {availableGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.nom}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="add-isResponsable"
                    checked={isResponsable}
                    onChange={(e) => setIsResponsable(e.target.checked)}
                    className="checkbox checkbox-sm"
                  />
                  <label
                    htmlFor="add-isResponsable"
                    className="text-xs text-slate-700 dark:text-slate-200"
                  >
                    Responsable du groupe
                  </label>
                </div>
                <div>
                  <label
                    className="block text-[0.7rem] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-300 mb-1"
                    htmlFor="add-poste"
                  >
                    Poste (optionnel)
                  </label>
                  <input
                    type="text"
                    id="add-poste"
                    value={poste}
                    onChange={(e) => setPoste(e.target.value)}
                    className="input input-bordered w-full text-sm bg-white dark:bg-slate-900 dark:text-slate-50"
                    placeholder="Ex : Membre, Référent, CTF..."
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    onClick={handleAddGroup}
                    className="btn-primary flex items-center gap-2 !px-3 !py-1.5"
                    text="Ajouter au groupe"
                    icon={FaPlus}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="px-5 pb-4 pt-2 sm:px-6 flex justify-end">
          <Button
            onClick={handleClose}
            className="btn-ghost flex items-center gap-2 !px-4 !py-2"
            text="Fermer"
            icon={FaTimes}
          />
        </div>
      </div>
    </div>
  );
};

export default ManageGroupModal;
